import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, Input, Button, Select, InputNumber, DatePicker, Spin, Divider, message,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, CloseOutlined, GiftOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import dayjs from 'dayjs';

const { Text } = Typography;

const Section = ({ title, description, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24, marginBottom: 16 }}>
    {title && <Text strong style={{ display: 'block', fontSize: 16, marginBottom: description ? 4 : 20 }}>{title}</Text>}
    {description && <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>{description}</Text>}
    {children}
  </div>
);

const generarNombre = (tipo, valor, montoMinimo) => {
  if (!tipo) return '';
  const min = montoMinimo > 0 ? ` en compras mayores a $${Number(montoMinimo).toFixed(0)}` : '';
  switch (tipo.codigo) {
    case 'envio_gratis': return `Envío gratis${min}`;
    case 'descuento_fijo': return `$${Number(valor).toFixed(0)} de descuento${min}`;
    case 'descuento_porcentaje': return `${valor}% de descuento${min}`;
    case 'precio_fijo': return `A $${Number(valor).toFixed(0)}${min}`;
    case '2x1': return `2x1${min}`;
    default: return tipo.nombre;
  }
};

export const AdminPromocionForm = () => {
  const { id } = useParams();
  const isEdit = !!id && id !== 'nueva';
  const navigate = useNavigate();
  const { theme, clientPath } = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';

  const [tipos, setTipos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    id_promocion: null, valor: 0, monto_minimo: 0,
    alcance: 'tienda', categoria_ids: [], producto_ids: [],
    fecha_desde: null, fecha_hasta: null, uso_maximo: null,
  });

  // Productos seleccionados (con nombre para mostrar)
  const [prodSeleccionados, setProdSeleccionados] = useState([]);
  const [prodResults, setProdResults] = useState([]);
  const [buscandoProd, setBuscandoProd] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    const promises = [
      api.promociones.admin.getTipos(),
      api.admin.catalogo.getCategorias(),
    ];
    if (isEdit) promises.push(api.promociones.admin.getAll());

    const results = await Promise.all(promises);
    if (results[0].status === 'success') setTipos(results[0].data || []);
    if (results[1].status === 'success') setCategorias(results[1].data || []);

    if (isEdit && results[2]?.status === 'success') {
      const promo = (results[2].data || []).find(p => p.id === Number(id));
      if (promo) {
        setForm({
          id_promocion: promo.id_promocion, valor: promo.valor, monto_minimo: promo.monto_minimo,
          alcance: promo.alcance || 'tienda',
          categoria_ids: promo.categoria_ids || [],
          producto_ids: promo.producto_ids || [],
          fecha_desde: promo.fecha_desde || null,
          fecha_hasta: promo.fecha_hasta || null,
          uso_maximo: promo.uso_maximo || null,
        });
        if (promo.alcance === 'productos' && promo.producto_ids?.length) {
          const res = await api.admin.catalogo.getProductos({ limit: 100 });
          if (res.status === 'success') {
            setProdSeleccionados((res.data?.items || []).filter(x => promo.producto_ids.includes(x.id)));
          }
        }
      }
    }
    setLoading(false);
  }, [id, isEdit]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_promocion) { message.warning('Seleccioná un tipo de promoción'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        producto_ids: form.alcance === 'productos' ? prodSeleccionados.map(p => p.id) : [],
      };
      if (isEdit) {
        await api.promociones.admin.editar(id, payload);
        message.success('Promoción actualizada');
      } else {
        await api.promociones.admin.crear(payload);
        message.success('Promoción creada');
      }
      navigate(clientPath('/admin/promociones'));
    } catch {
      message.error('Error al guardar');
    }
    setSaving(false);
  };

  const tipoSeleccionado = form.id_promocion ? tipos.find(t => t.id === form.id_promocion) : null;
  // El 2x1 tampoco lleva valor: el beneficio sale de la cantidad, no de un número.
  const sinValor = ['envio_gratis', '2x1'];
  const necesitaValor = tipoSeleccionado && !sinValor.includes(tipoSeleccionado.codigo);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spin size="large" /></div>;

  const ActionBar = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Button onClick={() => navigate(clientPath('/admin/promociones'))}>Cancelar</Button>
      <Button type="primary" htmlType="submit" form="promo-form" loading={saving}
        style={{ background: primary, borderColor: primary, color: primaryText }}>
        {saving ? 'Guardando...' : isEdit ? 'Guardar' : 'Crear promoción'}
      </Button>
    </div>
  );

  return (
    <div style={{ maxWidth: 620, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button type="text" icon={<ArrowLeftOutlined />}
            onClick={() => navigate(clientPath('/admin/promociones'))}
            style={{ padding: '0 4px' }} />
          <Text strong style={{ fontSize: 22 }}>
            {isEdit ? 'Editar promoción' : 'Nueva promoción'}
          </Text>
        </div>
        <ActionBar />
      </div>

      <form id="promo-form" onSubmit={handleSubmit}>

        {/* Tipo + Valor */}
        <Section title="Tipo y descuento">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Tipo de promoción</Text>
              <Select
                value={form.id_promocion || undefined}
                onChange={val => setForm(f => ({ ...f, id_promocion: val, valor: 0 }))}
                placeholder="Seleccionar tipo..."
                style={{ width: '100%' }}
                disabled={isEdit}
                options={tipos.map(t => ({ value: t.id, label: t.nombre }))}
              />
            </div>

            {form.id_promocion && (
              <div style={{ display: 'grid', gridTemplateColumns: necesitaValor ? '1fr 1fr' : '1fr', gap: 16 }}>
                {necesitaValor && (
                  <div>
                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
                      {tipoSeleccionado?.codigo === 'descuento_fijo' ? 'Monto ($)' : 'Porcentaje (%)'}
                    </Text>
                    <InputNumber
                      value={form.valor}
                      onChange={val => setForm(f => ({ ...f, valor: val ?? 0 }))}
                      min={0}
                      max={tipoSeleccionado?.codigo === 'descuento_porcentaje' ? 100 : undefined}
                      prefix={tipoSeleccionado?.codigo === 'descuento_fijo' ? '$' : undefined}
                      suffix={tipoSeleccionado?.codigo === 'descuento_porcentaje' ? '%' : undefined}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
                <div>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Compra mínima ($)</Text>
                  <InputNumber
                    value={form.monto_minimo}
                    onChange={val => setForm(f => ({ ...f, monto_minimo: val ?? 0 }))}
                    min={0} prefix="$" style={{ width: '100%' }}
                  />
                </div>
              </div>
            )}

            {/* Preview */}
            {form.id_promocion && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px' }}>
                <Text style={{ fontSize: 13, color: '#16a34a' }}>
                  <GiftOutlined style={{ marginRight: 6 }} />
                  {generarNombre(tipoSeleccionado, form.valor, form.monto_minimo)}
                </Text>
              </div>
            )}
          </div>
        </Section>

        {/* Alcance */}
        {form.id_promocion && (
          <Section title="Alcance" description="Definí a qué productos o categorías aplica esta promoción.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[
                { value: 'tienda', label: 'Toda la tienda' },
                { value: 'categorias', label: 'Categorías' },
                { value: 'productos', label: 'Productos' },
              ].map(opt => {
                const selected = form.alcance === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setForm(f => ({ ...f, alcance: opt.value, categoria_ids: [], producto_ids: [] }));
                      setProdSeleccionados([]);
                    }}
                    style={{
                      padding: '10px 0', borderRadius: 8,
                      border: `2px solid ${selected ? primary : '#e5e7eb'}`,
                      background: selected ? `${primary}08` : '#fff',
                      color: selected ? primary : '#6b7280',
                      fontWeight: selected ? 600 : 400,
                      fontSize: 13, cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {form.alcance === 'categorias' && (
              <Select
                mode="multiple"
                value={form.categoria_ids}
                onChange={val => setForm(f => ({ ...f, categoria_ids: val }))}
                placeholder="Seleccionar categorías..."
                style={{ width: '100%' }}
                options={categorias.map(c => ({ value: c.id, label: c.nombre }))}
              />
            )}

            {form.alcance === 'productos' && (
              <div>
                {prodSeleccionados.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {prodSeleccionados.map(p => (
                      <span key={p.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', borderRadius: 6, background: '#f3f4f6', fontSize: 13,
                      }}>
                        {p.nombre}
                        <button type="button" onClick={() => setProdSeleccionados(prev => prev.filter(x => x.id !== p.id))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: 0, lineHeight: 1 }}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Select
                  showSearch
                  value={undefined}
                  placeholder="Buscar producto..."
                  filterOption={false}
                  onSearch={async (val) => {
                    if (!val.trim()) { setProdResults([]); return; }
                    setBuscandoProd(true);
                    try {
                      const res = await api.admin.catalogo.getProductos({ q: val, limit: 10 });
                      if (res.status === 'success') {
                        setProdResults((res.data?.items || []).filter(p => !prodSeleccionados.find(s => s.id === p.id)));
                      }
                    } catch {}
                    setBuscandoProd(false);
                  }}
                  onSelect={(val) => {
                    const prod = prodResults.find(p => p.id === val);
                    if (prod) setProdSeleccionados(prev => [...prev, prod]);
                    setProdResults([]);
                  }}
                  loading={buscandoProd}
                  notFoundContent={buscandoProd ? <Spin size="small" /> : null}
                  style={{ width: '100%' }}
                  options={prodResults.map(p => ({ value: p.id, label: `${p.nombre}${p.codigo ? ` (${p.codigo})` : ''}` }))}
                />
              </div>
            )}
          </Section>
        )}

        {/* Límites */}
        {form.id_promocion && (
          <Section title="Límites" description="Opcional. Definí vigencia por fecha o cantidad máxima de usos.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Desde</Text>
                <DatePicker
                  value={form.fecha_desde ? dayjs(form.fecha_desde) : null}
                  onChange={val => setForm(f => ({ ...f, fecha_desde: val ? val.toISOString() : null }))}
                  style={{ width: '100%' }}
                  placeholder="Sin límite"
                />
              </div>
              <div>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Hasta</Text>
                <DatePicker
                  value={form.fecha_hasta ? dayjs(form.fecha_hasta) : null}
                  onChange={val => setForm(f => ({ ...f, fecha_hasta: val ? val.toISOString() : null }))}
                  style={{ width: '100%' }}
                  placeholder="Sin límite"
                />
              </div>
            </div>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Cantidad máxima de usos</Text>
              <InputNumber
                value={form.uso_maximo}
                onChange={val => setForm(f => ({ ...f, uso_maximo: val || null }))}
                min={1}
                placeholder="Ilimitado"
                style={{ width: '100%' }}
              />
            </div>
          </Section>
        )}

        {/* Action bar bottom */}
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button onClick={() => navigate(clientPath('/admin/promociones'))}>Cancelar</Button>
          <Button type="primary" htmlType="submit" loading={saving}
            style={{ background: primary, borderColor: primary, color: primaryText }}>
            {saving ? 'Guardando...' : isEdit ? 'Guardar' : 'Crear promoción'}
          </Button>
        </div>
      </form>
    </div>
  );
};
